import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Paperclip } from "lucide-react";
import {
	TableRow,
	TableCell,
	AttachmentThumbnails,
	AttachmentGallery,
} from "@hotelise/ui";
import type { GalleryFile, ThumbnailFile } from "@hotelise/ui";

import { Expense } from "interfaces/expenses";
import FileUploadService from "services/storage/fileUpload";

interface Props {
	data: Expense;
	showProperty?: boolean;
	visibleColumnIds?: string[];
	isMobile?: boolean;
}

const getFileTypeFromUrl = (url: string): string => {
	const lowerUrl = url.toLowerCase();
	if (lowerUrl.endsWith(".jpg") || lowerUrl.endsWith(".jpeg")) return "image/jpeg";
	if (lowerUrl.endsWith(".png")) return "image/png";
	if (lowerUrl.endsWith(".gif")) return "image/gif";
	if (lowerUrl.endsWith(".webp")) return "image/webp";
	if (lowerUrl.endsWith(".pdf")) return "application/pdf";
	if (lowerUrl.endsWith(".mp4")) return "video/mp4";
	if (lowerUrl.endsWith(".webm")) return "video/webm";
	if (lowerUrl.endsWith(".mov")) return "video/quicktime";
	return "application/octet-stream";
};

const ExpenseTableItem = (props: Props): JSX.Element => {
	const { data, showProperty, visibleColumnIds, isMobile } = props;

	const {
		name,
		description,
		amount,
		expense_date,
		currency,
		receiptUrl,
		supplier,
		receiptUrls,
	} = data;

	const [isGalleryOpen, setIsGalleryOpen] = useState(false);
	const [galleryFiles, setGalleryFiles] = useState<GalleryFile[]>([]);
	const [thumbnailFiles, setThumbnailFiles] = useState<ThumbnailFile[]>([]);

	const effectiveReceiptUrls = (
		receiptUrls && receiptUrls.length > 0
			? receiptUrls
			: receiptUrl
				? [receiptUrl]
				: []
	).filter((url) => url && url.trim().length > 0);

	useEffect(() => {
		const loadFiles = async () => {
			if (effectiveReceiptUrls.length === 0) {
				setGalleryFiles([]);
				setThumbnailFiles([]);
				return;
			}

			const files = await Promise.all(
				effectiveReceiptUrls.map(async (url, index) => {
					try {
						const signedUrl = await FileUploadService.getSignedUrl(url);
						const fileName = url.split("/").pop() || `File ${index + 1}`;
						const fileType = getFileTypeFromUrl(url);
						return {
							id: url,
							name: fileName,
							url: signedUrl,
							size: 0,
							type: fileType,
						} as GalleryFile;
					} catch (error) {
						console.error("Failed to get signed URL:", error);
						return null;
					}
				})
			);

			const validFiles = files.filter((f) => f !== null) as GalleryFile[];
			setGalleryFiles(validFiles);

			const thumbs = validFiles.map(
				(file) =>
					({
						id: file.id,
						name: file.name,
						url: file.url,
						type: file.type,
					}) as ThumbnailFile
			);
			setThumbnailFiles(thumbs);
		};

		loadFiles();
	}, [receiptUrls, receiptUrl]);

	const handleOpenGallery = () => {
		if (effectiveReceiptUrls.length === 0) return;
		setIsGalleryOpen(true);
	};

	const isVisible = (id: string) => {
		if (!visibleColumnIds) return true;
		return visibleColumnIds.includes(id);
	};

	const renderGallery = () =>
		galleryFiles.length > 0 ? (
			<AttachmentGallery
				open={isGalleryOpen}
				onOpenChange={setIsGalleryOpen}
				files={galleryFiles}
				title="Expense Attachments"
				initialIndex={0}
			/>
		) : null;

	if (isMobile) {
		const formattedAmount = amount != null ? `${currency} ${amount}` : "-";
		const formattedDate = dayjs(expense_date).format("MMM DD, YYYY");

		return (
			<>
				<article className="rounded-lg border bg-card p-3 shadow-sm space-y-1.5">
					{/* Row 1: Name + Amount */}
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-1.5 min-w-0">
							<span className="font-medium text-sm truncate">{name || "-"}</span>
						</div>
						<span className="font-medium text-sm tabular-nums shrink-0 ml-2">{formattedAmount}</span>
					</div>
					{/* Row 2: Property + Date */}
					<div className="flex items-center justify-between text-xs text-muted-foreground">
						{showProperty && data.propertyName ? (
							<span className="text-primary">{data.propertyName}</span>
						) : (
							<span />
						)}
						<span>{formattedDate}</span>
					</div>
					{/* Supplier */}
					{supplier && (
						<div className="flex justify-between text-xs">
							<span className="text-muted-foreground">Supplier</span>
							<span>{supplier}</span>
						</div>
					)}
					{/* Description */}
					{description && (
						<p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
					)}
					{/* Attachments */}
					<div className="flex items-center pt-0.5">
						{thumbnailFiles.length > 0 ? (
							<AttachmentThumbnails
								files={thumbnailFiles}
								max={3}
								size={28}
								onClick={handleOpenGallery}
							/>
						) : (
							<span className="text-xs text-muted-foreground/50">No attachments</span>
						)}
					</div>
				</article>
				{renderGallery()}
			</>
		);
	}

	return (
		<>
			<TableRow>
				{isVisible("name") && (
					<TableCell className="font-medium text-sm whitespace-nowrap">
						<div className="flex items-center gap-1.5">
							{effectiveReceiptUrls.length > 0 && (
								<Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
							)}
							<span>{name || "-"}</span>
						</div>
					</TableCell>
				)}
				{isVisible("supplier") && (
					<TableCell className="text-sm whitespace-nowrap">{supplier || "-"}</TableCell>
				)}
				{showProperty && isVisible("property") && (
					<TableCell className="text-sm text-primary font-medium whitespace-nowrap">{data.propertyName || ""}</TableCell>
				)}
				{isVisible("description") && (
					<TableCell className="text-sm max-w-[420px] truncate text-muted-foreground">{description || "-"}</TableCell>
				)}
				{isVisible("amount") && (
					<TableCell className="text-sm tabular-nums whitespace-nowrap">{currency} {amount}</TableCell>
				)}
				{isVisible("date") && (
					<TableCell className="text-sm whitespace-nowrap">{dayjs(expense_date).format("D MMM, YYYY")}</TableCell>
				)}
				{isVisible("receipt") && (
					<TableCell>
						{thumbnailFiles.length > 0 ? (
							<AttachmentThumbnails
								files={thumbnailFiles}
								max={3}
								size={28}
								onClick={handleOpenGallery}
							/>
						) : (
							<span className="text-muted-foreground">&mdash;</span>
						)}
					</TableCell>
				)}
			</TableRow>
			{renderGallery()}
		</>
	);
};

export default ExpenseTableItem;
